/**
 * International phone input: country dial-code select + national number field.
 *
 * Validation is deliberately coarse — a digit-count range rather than
 * per-country rules — so it never rejects a number someone can actually be
 * reached on. It reports through the native Constraint Validation API, so the
 * browser blocks submission the same way it does for the other required fields.
 */

// A leading `+` is allowed so a pasted international number still validates.
const ALLOWED_CHARS = /^\+?[\d\s()./-]+$/;

// National number without the trunk prefix, e.g. "151 23456789" -> 10 digits.
const MIN_NATIONAL_DIGITS = 4;
const MAX_NATIONAL_DIGITS = 14;

// E.164 caps the country code plus national number at 15 digits.
const MIN_INTERNATIONAL_DIGITS = 7;
const MAX_INTERNATIONAL_DIGITS = 15;

function digitsOf(value) {
  return value.replace(/\D/g, "");
}

/**
 * Digits of a national number, without the leading 0 trunk prefix — that 0 is
 * replaced by the country dial code, so "0151 …" and "151 …" are the same number.
 */
function nationalDigits(value) {
  return digitsOf(value).replace(/^0+/, "");
}

function isValid(raw) {
  if (!ALLOWED_CHARS.test(raw)) {
    return false;
  }

  if (raw.startsWith("+")) {
    const digits = digitsOf(raw);
    return (
      digits.length >= MIN_INTERNATIONAL_DIGITS &&
      digits.length <= MAX_INTERNATIONAL_DIGITS
    );
  }

  const digits = nationalDigits(raw);
  return (
    digits.length >= MIN_NATIONAL_DIGITS && digits.length <= MAX_NATIONAL_DIGITS
  );
}

function validate(input) {
  const raw = input.value.trim();

  // An empty field is the `required` attribute's job to report, not ours.
  if (!raw || isValid(raw)) {
    input.setCustomValidity("");
    return;
  }

  input.setCustomValidity(
    document.documentElement.lang === "en"
      ? "Please enter a valid phone number."
      : "Bitte eine gültige Telefonnummer eingeben.",
  );
}

/**
 * The number as one E.164-style string, e.g. "+4915123456789".
 * Returns null when the form has no phone input, "" when it was left empty.
 */
export function fullPhoneValue(form) {
  const wrapper = form.querySelector("[data-phone-input]");
  if (!wrapper) {
    return null;
  }

  const input = wrapper.querySelector("[data-phone-number]");
  const prefix = wrapper.querySelector("[data-phone-prefix]");
  if (!input || !prefix) {
    return null;
  }

  const raw = input.value.trim();
  if (!raw) {
    return "";
  }

  // Already international — the typed country code wins over the select.
  if (raw.startsWith("+")) {
    return `+${digitsOf(raw)}`;
  }

  return `${prefix.value}${nationalDigits(raw)}`;
}

export function init() {
  document.querySelectorAll("[data-phone-input]").forEach((wrapper) => {
    const input = wrapper.querySelector("[data-phone-number]");
    if (!input) {
      return;
    }

    const revalidate = () => validate(input);
    input.addEventListener("input", revalidate);
    input.addEventListener("blur", revalidate);
  });
}
